import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { units, Unit } from '../data/units';
import { getUnitProgress } from '../data/progress';

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [loginCount, setLoginCount] = useState<number | null>(null);

  useEffect(() => {
    fetch('/cdn-cgi/access/get-identity')
      .then(r => r.json())
      .then(data => {
        if (data.email) {
          setUserEmail(data.email);
          setUserName(data.name || data.email.split('@')[0]);
          // Increment login counter
          fetch('/api/login-count', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.email }),
          })
            .then(r => r.json())
            .then(d => setLoginCount(d.count))
            .catch(() => {});
        }
      })
      .catch(() => {}); // Not behind Access, silently ignore
  }, []);

  const handleUnitClick = (unitId: string) => {
    navigate(`/unit/${unitId}`);
  };

  return (
    <Container>
      {userEmail && (
        <UserBar>
          <UserAvatar>{(userName || userEmail)[0].toUpperCase()}</UserAvatar>
          <UserInfo>
            <UserName>{userName}</UserName>
            <UserEmail>{userEmail}</UserEmail>
          </UserInfo>
          <LoginCounter>
            <LoginCountNumber>{loginCount !== null ? loginCount : '\u2014'}</LoginCountNumber>
            <LoginCountLabel>visits</LoginCountLabel>
          </LoginCounter>
        </UserBar>
      )}
      <HeaderArea>
        <HeaderContent>
          <TitleBlock>
            <AppTitle>AP Physics 1</AppTitle>
            <Subtitle>Master every concept, ace the exam</Subtitle>
          </TitleBlock>
          <ProgressLink onClick={() => navigate('/progress')}>
            <ProgressIcon>📊</ProgressIcon>
            <ProgressLabel>My Progress</ProgressLabel>
          </ProgressLink>
        </HeaderContent>
      </HeaderArea>

      <Main>
        <ConceptMapLink onClick={() => navigate('/concept-map')}>
          <ConceptMapIcon>🗺️</ConceptMapIcon>
          <ConceptMapLinkText>
            <ConceptMapLinkTitle>Concept Map</ConceptMapLinkTitle>
            <ConceptMapLinkSub>See how all physics concepts connect</ConceptMapLinkSub>
          </ConceptMapLinkText>
          <ConceptMapArrow>→</ConceptMapArrow>
        </ConceptMapLink>
        <UnitGrid>
          {units.map((unit) => {
            const conceptIds = unit.concepts.map((c) => c.id);
            const progress = getUnitProgress(unit.id, conceptIds);
            const progressPct =
              progress.total > 0
                ? Math.round((progress.completed / progress.total) * 100)
                : 0;

            return (
              <UnitCard
                key={unit.id}
                $accentColor={unit.color}
                onClick={() => handleUnitClick(unit.id)}
              >
                <CardAccent $color={unit.color} />
                <CardBody>
                  <CardHeader>
                    <UnitIcon>{unit.icon}</UnitIcon>
                    <UnitMeta>
                      <UnitNumber $color={unit.color}>
                        Unit {unit.number}
                      </UnitNumber>
                      <UnitTitle>{unit.title}</UnitTitle>
                    </UnitMeta>
                  </CardHeader>

                  <UnitDescription>{unit.description}</UnitDescription>

                  <ProgressSection>
                    <ProgressInfo>
                      <ProgressText>
                        {progress.completed} / {progress.total} concepts
                      </ProgressText>
                      <ProgressPercent>{progressPct}%</ProgressPercent>
                    </ProgressInfo>
                    <ProgressBarTrack>
                      <ProgressBarFill
                        $pct={progressPct}
                        $color={unit.color}
                      />
                    </ProgressBarTrack>
                  </ProgressSection>

                  {progress.avgScore > 0 && (
                    <QuizScore>
                      <ScoreLabel>Avg quiz score</ScoreLabel>
                      <ScoreValue $color={unit.color}>
                        {progress.avgScore}%
                      </ScoreValue>
                    </QuizScore>
                  )}
                </CardBody>
              </UnitCard>
            );
          })}
        </UnitGrid>
      </Main>
    </Container>
  );
};

export default HomeScreen;

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const HeaderArea = styled.header`
  background: linear-gradient(135deg, #1a73e8 0%, #1557b0 50%, #0d47a1 100%);
  padding: 40px 24px 48px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -5%;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    pointer-events: none;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  z-index: 1;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AppTitle = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.5px;
  line-height: 1.1;

  @media (max-width: 600px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const ProgressLink = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 10px 18px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ProgressIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const ProgressLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
`;

const Main = styled.main`
  max-width: 1200px;
  margin: -24px auto 0;
  padding: 0 24px 60px;
  position: relative;
  z-index: 2;
`;

const UnitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const UnitCard = styled.div<{ $accentColor: string }>`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const CardAccent = styled.div<{ $color: string }>`
  height: 4px;
  background: ${(props) => props.$color};
  flex-shrink: 0;
`;

const CardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const UnitIcon = styled.span`
  font-size: 36px;
  line-height: 1;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnitMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const UnitNumber = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const UnitTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.25;
`;

const UnitDescription = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: #5f6577;
  margin: 0;
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const ProgressText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8890a4;
`;

const ProgressPercent = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #5f6577;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #eef0f5;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(props) => props.$pct}%;
  background: ${(props) => props.$color};
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const QuizScore = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 8px 12px;
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8890a4;
`;

const ScoreValue = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.$color};
`;

const ConceptMapLink = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto 24px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #37474f, #455a64);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  text-align: left;
  color: #fff;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }
`;

const ConceptMapIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const ConceptMapLinkText = styled.div`
  flex: 1;
`;

const ConceptMapLinkTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const ConceptMapLinkSub = styled.div`
  font-size: 13px;
  opacity: 0.8;
  margin-top: 2px;
`;

const ConceptMapArrow = styled.span`
  font-size: 20px;
  opacity: 0.7;
`;

const UserBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
  background: #1a1a2e;
  color: #fff;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 11px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoginCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 6px 14px;
`;

const LoginCountNumber = styled.div`
  font-size: 18px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const LoginCountLabel = styled.div`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
`;
